<template>
  <div
    class="pdp-grid"
    :class="[
      Boolean(isMerch) ? 'merch-grid' : 'regular-grid',
      '-my-2 gap-x-4 md:-mt-8',
    ]"
  >
    <ProductDetailGallery
      class="gallery"
      :product="product"
      :selected-variant="selectedVariant"
      :has-siblings="hasSiblings"
      :is-eyewear="true"
      :show-vto="true"
      :show-siblings="false"
      @on-vto-action="
        ({ product, variant }) => $emit('onVtoAction', { product, variant })
      "
      @on-vto-open="() => $emit('onVtoOpen')"
      @on-vto-close="() => $emit('onVtoClose')"
    >
      <template #gallery-header>
        <div class="flex-col gap-6 bg-gray-150 lg:hidden">
          <div class="flex flex-col gap-6 bg-transparent">
            <div class="flex flex-row gap-4">
              <GoBackLinkRx :to="backUrl" />
              <div>
                <ProductBadges
                  :product="product"
                  :selected-variant="selectedVariant"
                />
              </div>
            </div>

            <ProductHeaderInfo
              :product="product"
              :variant="selectedVariant"
              :product-name="productName"
              :product-color="productColor"
              viewport="mobile"
            />
          </div>
        </div>
        <ProductBadges
          class="hidden lg:block"
          :product="product"
          :selected-variant="selectedVariant"
        />
      </template>
    </ProductDetailGallery>
    <div class="actions">
      <div
        class="relative max-w-[calc(100vw-2rem)] lg:flex lg:flex-col lg:gap-6"
      >
        <div class="hidden flex-col gap-6 bg-transparent lg:flex">
          <div class="flex flex-row">
            <GoBackLinkRx :to="backUrl" />
          </div>

          <ProductHeaderInfo
            :product="product"
            :variant="selectedVariant"
            :product-name="productName"
            :product-color="productColor"
            viewport="desktop"
          />
        </div>
        <slot name="actions" />
      </div>
    </div>
    <div v-if="!isMerch" class="brand">
      <div v-if="brand" class="flex flex-col">
        <Headline tag="span" look="h3">
          {{ $t('pdp.product_detail_brand') }}
        </Headline>
        <p>
          {{ brand }}
        </p>
      </div>
    </div>
    <div v-if="productDetailProperties.length" class="attribute-table">
      <ProductDetailAttributesTable
        :product="product"
        :detail-properties="productDetailProperties"
        :active-variant="selectedVariant"
        :translations="detailNameMapping"
        :split-by="tableColumnCount"
      />
    </div>
    <div class="description has-[div]:my-4 has-[div]:lg:my-8">
      <ProductDetailTexts
        :get-attribute="getAttribute"
        type="list"
        class="mb-4"
        :list-spacing="2"
      />

      <ProductDetailTexts :get-attribute="getAttribute" type="short" />

      <ProductDetailTexts :get-attribute="getAttribute" type="long" />
    </div>
    <div v-if="product.attributes || slots.manuals" class="manual w-full">
      <slot name="manuals" />
      <ProductDetailManufacturerDetails
        v-if="product.attributes"
        :product-attributes="product.attributes"
      />
    </div>

    <div v-if="shape && !isMonoLens && selectedVariant" class="measure">
      <Headline class="font-medium" tag="span" look="h3">
        {{ $t('pdp.measures_headline') }}
      </Headline>
      <GlassesMeasures :product="product" :current-variant="selectedVariant" />
    </div>
  </div>
</template>

<script setup lang="ts">
// https://aboutyou.atlassian.net/browse/SCFIM-1621
import type { Product, Variant } from '@scayle/storefront-nuxt'
import type { AdvancedAttribute, AttributeGroup } from '@aboutyou/backbone'

const props = defineProps({
  product: {
    type: Object as PropType<Product>,
    required: true,
  },
  isMerch: {
    type: Boolean,
    default: false,
  },
  productDetailProperties: {
    type: Array as PropType<string[]>,
    required: false,
    default: () => [],
  },
  detailNameMapping: {
    type: Object as PropType<Record<string, string>>,
    required: true,
  },
  selectedVariant: {
    type: Object as PropType<Variant | undefined>,
    default: undefined,
  },
  hasSiblings: {
    type: Boolean as PropType<boolean | undefined>,
    default: undefined,
  },
  productName: {
    type: String as PropType<string>,
    required: true,
  },
  productColor: {
    type: String as PropType<string>,
    required: true,
  },
  backUrl: {
    type: String as PropType<string>,
    required: true,
  },
  getAttribute: {
    type: Function as PropType<
      (
        key: string,
        full?: boolean,
      ) => string | AttributeGroup | AdvancedAttribute | null | undefined
    >,
    required: true,
  },
})

defineEmits<{
  (e: 'onVtoAction', value: { product: Product; variant: Variant }): void
  (e: 'onVtoClose' | 'onVtoOpen'): void
}>()

const brand = computed(() =>
  getFirstAttributeLabel(props.product.attributes, 'brand'),
)

const shape = computed(() =>
  getFirstAttributeLabel(props.product.attributes, 'shape'),
)

const isMonoLens = computed(() => isMonoLensGlassProduct(props.product))

const { isLessThan } = useViewport()

const tableColumnCount = computed(() => {
  return isLessThan('lg') ? 1 : 3
})

const slots = useSlots()
</script>

<style lang="scss" scoped>
.pdp-grid {
  display: grid;
  gap: 2rem;
}

.regular-grid {
  grid-template-areas:
    'gallery'
    'actions'
    'brand'
    'measure'
    'attribute-table'
    'description'
    'manual';

  @media screen and (width >= 640px) {
    row-gap: 2.5rem;
    grid-template-columns: repeat(2, 1fr);
    grid-template-areas:
      'gallery gallery'
      'brand actions'
      'measure actions'
      'attribute-table actions'
      '. actions'
      'description description'
      'manual manual';
  }

  @media screen and (width >= 1024px) {
    row-gap: 2.5rem;
    grid-template-columns: repeat(3, 1fr);
    grid-template-areas:
      'gallery gallery actions'
      'brand measure actions'
      'attribute-table attribute-table actions'
      'description description actions'
      'manual manual actions';
  }
}

.merch-grid {
  grid-template-areas:
    'gallery'
    'actions'
    'description'
    'manual';

  @media screen and (width >= 640px) {
    row-gap: 2.5rem;
    grid-template-columns: repeat(2, 1fr);
    grid-template-areas:
      'gallery gallery'
      'description actions'
      'description actions'
      'manual actions';
  }

  @media screen and (width >= 1024px) {
    row-gap: 2.5rem;
    grid-template-columns: repeat(3, 1fr);
    grid-template-areas:
      'gallery gallery actions'
      'description description actions'
      'description description actions'
      'manual manual actions';
  }
}

.gallery {
  grid-area: gallery;
}

.actions {
  grid-area: actions;
}

.brand {
  grid-area: brand;
}

.measure {
  grid-area: measure;
}

.attribute-table {
  grid-area: attribute-table;
}

.description {
  grid-area: description;
}

.manual {
  grid-area: manual;
}
</style>
